/**
 * El workspace de Cobros.
 *
 * Antes eran cinco destinos sueltos en el menú —Generar mes, Cobros, Pagos,
 * Facturación, Conciliación— que en realidad son etapas del mismo ciclo. Acá
 * son subnavegación de un solo lugar, y **generar el mes es una acción**, no un
 * sitio al que uno va.
 */

import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { periodLabel, startOfMonth, today } from '@cleanblue/shared';
import { useApi } from '@/lib/hooks';
import { Badge, PageHead } from '@/components/ui';

interface Counts {
  counts: { submissions_pending: number; charges_needing_review: number; overdue: number };
}

const TABS = [
  // Primero lo que hay que HACER; después el diagnóstico mes a mes.
  { to: '/admin/cobros/por-hacer', label: 'Por hacer' },
  { to: '/admin/cobros', label: 'Matriz anual', end: true },
  { to: '/admin/cobros/mes', label: 'Mes' },
  { to: '/admin/cobros/pagos', label: 'Pagos', badge: 'submissions_pending' as const },
  { to: '/admin/cobros/facturacion', label: 'Facturación' },
  { to: '/admin/cobros/conciliacion', label: 'Conciliación', badge: 'charges_needing_review' as const },
];

export function CobrosLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dashboard = useApi<Counts>('/admin/dashboard', { staleTime: 60_000 });
  const counts = dashboard.data?.counts;

  const period = startOfMonth(today()).slice(0, 7);

  return (
    <>
      <PageHead
        title="Cobros"
        subtitle={`Todo el ciclo del mes, de la generación a la conciliación. Período en curso: ${periodLabel(`${period}-01`)}`}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate('/admin/cobros/generar')}
          >
            Generar el mes
          </button>
        }
      />

      <div className="tabs mb-3">
        {TABS.map((tab) => {
          const badge = tab.badge ? counts?.[tab.badge] ?? 0 : 0;
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `tab${isActive ? ' is-active' : ''}`}
            >
              {tab.label}
              {badge > 0 && <Badge tone="warn" plain> {badge}</Badge>}
            </NavLink>
          );
        })}
      </div>

      {/* La clave por ruta hace que cada subpantalla monte limpia en vez de
          heredar el estado de la anterior. */}
      <Outlet key={location.pathname} />
    </>
  );
}
